import React, { useState, useRef, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import api from "../services/api";
import useAuth from "../context/AuthContext";
import ThemeToggle from "./ThemeToggle";
import { useTheme } from "../context/ThemeContext";
import NewClientEngagementModal from "./NewClientEngagementModal";


import dashboardDark from "../assets/Dashboard.png";
import dashboardLight from "../assets/Dashboard-white.png";
import itaxQuestDark from "../assets/iTaxQuest.png";
import itaxQuestLight from "../assets/iTaxQuest-White.png";
import itaxCompDark from "../assets/iTaxComp.png";
import itaxCompLight from "../assets/iTaxComp-white.png";
import feedbackDark from "../assets/Feedback.png";
import feedbackLight from "../assets/Feedback-white.png";
import notificationDark from "../assets/Notifications.png";
import notificationLight from "../assets/Notification-white.png";
import adminAvatar from "../assets/Adminavatar.jpg";
import logoutDark from "../assets/logout.avif";
import logoutLight from "../assets/logout-white.avif";

interface SidebarProps {
  activeKey?: "dashboard" | "itaxquest" | "eci" | "formc" | "feedback" | "notifications";
  compact?: boolean;
}

export const SIDEBAR_WIDTH_FULL = 240;
export const SIDEBAR_WIDTH_COMPACT = 80;

export default function Sidebar({ activeKey = "dashboard", compact = true }: SidebarProps) {
  const navigate = useNavigate();
  const [openCompMenu, setOpenCompMenu] = useState(activeKey === "eci" || activeKey === "formc");
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const menuRef = useRef<HTMLDivElement | null>(null);
  const { user } = useAuth();
  const location = useLocation();
  const [showDashboardConfirm, setShowDashboardConfirm] = useState(false);
  const { theme } = useTheme();
  const [showEngagementModal, setShowEngagementModal] = useState(false);

  useEffect(() => {
    function onDocClick(e: MouseEvent) {
      if (!menuRef.current) return;
      if (!menuRef.current.contains(e.target as Node)) setOpenCompMenu(false);
    }
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth <= 1024);
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  const handleStartEci = async () => {
    try {
      const res = await api.post("/engagement", {
        clientId: 1,
        yearOfAssessment: 2026,
        formType: "ECI",
      });
      const engagementId = res.data?.data?.id;
      if (!engagementId) throw new Error("Engagement ID not returned");
      navigate(`/eci/${engagementId}`);
    } catch (err) {
      console.error("Failed to start ECI", err);
      alert("Unable to start ECI. Please try again.");
    }
  };

  const handleStartFormC = async () => {
    try {
      const res = await api.post("/engagement", {
        clientId: 1,
        yearOfAssessment: 2026,
        formType: "FORM_C",
      });
      const engagementId = res.data?.data?.id;
      if (!engagementId) throw new Error("Engagement ID not returned");
      navigate(`/formc/${engagementId}`);
    } catch (err) {
      console.error("Failed to start Form C", err);
      alert("Unable to start Form C. Please try again.");
    }
  };

  const handleCreateEngagement = async (
    companyName: string,
    taxRef: string,
    functionalCurrency: string,
    yearOfAssessment: string,
    basisPeriodStart: string,
    basisPeriodEnd: string
  ) => {
    setShowEngagementModal(false);
    try {
      const ya = Number(yearOfAssessment) || new Date().getFullYear();
      const payload = {
        clientId: 1,
        yearOfAssessment: ya,
        formType: "ECI",
      };
      const res = await api.post("/engagement", payload);
      const engagementId = res.data?.data?.id;
      if (!engagementId) throw new Error("Engagement ID not returned");

      try {
        await api.put(`/engagement/${engagementId}/client-profile`, {
          entityName: companyName,
          uen: taxRef,
          yearOfAssessment: ya,
          functionalCurrency: functionalCurrency || "SGD",
          basisPeriodStart,
          basisPeriodEnd,
        });
      } catch (profileErr) {
        console.warn("[Engagement] Failed to save client profile (non-fatal):", profileErr);
      }

      navigate(`/eci/${engagementId}`);
    } catch (err) {
      console.error("Failed to create engagement", err);
      alert("Failed to create case.");
    }
  };

  const dashboardPath = user?.role === "approver" ? "/approver" : "/preparer";
  const isOnDashboard = location.pathname === dashboardPath;

  const handleLogoClick = () => {
    if (isOnDashboard) return;
    setShowDashboardConfirm(true);
  };

  const confirmRedirectToDashboard = () => {
    setShowDashboardConfirm(false);
    navigate(user?.role === "approver" ? "/approver" : "/preparer");
  };

  const cancelRedirect = () => setShowDashboardConfirm(false);

  // Nav item — compact shows icon + label stacked, full shows icon + label inline
  const NavItem = ({
    iconDark,
    iconLight,
    label,
    active,
    onClick,
    title,
  }: {
    iconDark: string;
    iconLight: string;
    label: string;
    active: boolean;
    onClick: () => void;
    title?: string;
  }) => {
    const [hovered, setHovered] = useState(false);

    if (compact) {
      return (
        <div
          title={title || label}
          onClick={onClick}
          onMouseEnter={() => setHovered(true)}
          onMouseLeave={() => setHovered(false)}
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 5,
            padding: "10px 6px",
            borderRadius: 12,
            cursor: "pointer",
            background: active
              ? "var(--step-active-bg)"
              : hovered
              ? "var(--card-bg)"
              : "transparent",
            transition: "background 0.15s ease",
            minWidth: 56,
          }}
        >
          <img
            src={theme === "dark" ? iconDark : iconLight}
            style={{ width: 26, height: 26, objectFit: "contain" }}
            alt={label}
          />
          <span
            style={{
              fontSize: 11,
              fontWeight: active ? 600 : 400,
              color: active ? "#60a5fa" : "var(--color)",
              letterSpacing: "0.01em",
              lineHeight: 1,
              textAlign: "center",
              opacity: active ? 1 : 0.75,
            }}
          >
            {label}
          </span>
        </div>
      );
    }

    // Full mode — icon + label inline
    return (
      <div
        title={title || label}
        onClick={onClick}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        style={{
          padding: "12px 16px",
          borderRadius: 10,
          display: "flex",
          alignItems: "center",
          gap: 12,
          cursor: "pointer",
          fontSize: 15,
          fontWeight: active ? 600 : 500,
          color: active ? "#60a5fa" : "var(--color)",
          background: active
            ? "var(--bg-color)"
            : hovered
            ? "var(--card-bg)"
            : "transparent",
          transition: "background 0.15s ease",
        }}
      >
        <img
          src={theme === "dark" ? iconDark : iconLight}
          style={{ width: 18, height: 18, objectFit: "contain", flex: "0 0 18px" }}
          alt={label}
        />
        {label}
      </div>
    );
  };

  return (
    <>
      {isMobile && !sidebarOpen && (
        <button
          onClick={() => setSidebarOpen(true)}
          style={{
            position: "fixed",
            top: 20,
            left: 20,
            zIndex: 9999,
            background: "var(--step-connector)",
            border: "none",
            borderRadius: 8,
            padding: "10px 14px",
            fontSize: 22,
            cursor: "pointer",
          }}
        >
          ☰
        </button>
      )}

      <aside
        style={{
          width: compact ? SIDEBAR_WIDTH_COMPACT : SIDEBAR_WIDTH_FULL,
          background: "var(--bg-color)",
          borderRight: "1px solid var(--border)",
          padding: compact ? "20px 8px" : 24,
          display: "flex",
          flexDirection: "column",
          alignItems: compact ? "center" : "stretch",
          minHeight: "100vh",
          position: "fixed",
          top: 0,
          left: isMobile ? (sidebarOpen ? 0 : -260) : 0,
          transition: "left 0.3s ease, width 0.2s ease",
          zIndex: 5000,
          boxSizing: "border-box",
        }}
      >
        {isMobile && (
          <button
            onClick={() => setSidebarOpen(false)}
            style={{
              alignSelf: "flex-end",
              background: "var(--step-connector)",
              border: "none",
              fontSize: 22,
              cursor: "pointer",
              marginBottom: 10,
            }}
          >
            ✕
          </button>
        )}

        {/* Theme toggle — full mode only */}
        {!compact && (
          <div style={{ position: "absolute", top: 20, right: 66, zIndex: 10 }}>
            <ThemeToggle />
          </div>
        )}

        {/* LOGO */}
        <div
          style={{
            marginBottom: compact ? 28 : 80,
            display: "flex",
            justifyContent: "center",
            width: "100%",
          }}
        >
          <img
            src="/itaxlogo3.png"
            alt="logo"
            style={{
              height: compact ? 30 : 40,
              width: compact ? 30 : 99,
              cursor: "pointer",
              objectFit: "contain",
            }}
            onClick={handleLogoClick}
          />
        </div>

        {/* NAV */}
        <nav
          style={{
            display: "flex",
            flexDirection: "column",
            gap: compact ? 12 : 8,
            width: "100%",
            alignItems: compact ? "center" : "stretch",
          }}
        >
          <NavItem
            iconDark={dashboardDark}
            iconLight={dashboardLight}
            label="Home"
            active={activeKey === "dashboard"}
            onClick={() => { navigate("/preparer"); if (isMobile) setSidebarOpen(false); }}
          />
          <NavItem
            iconDark={itaxCompDark}
            iconLight={itaxCompLight}
            label="iTax Comp"
            active={activeKey === "eci" || activeKey === "formc"}
            onClick={() => { setShowEngagementModal(true); if (isMobile) setSidebarOpen(false); }}
          />
          <NavItem
            iconDark={itaxQuestDark}
            iconLight={itaxQuestLight}
            label="iTax Quest"
            active={activeKey === "itaxquest"}
            onClick={() => { navigate("/itaxquest"); if (isMobile) setSidebarOpen(false); }}
          />
          <NavItem
            iconDark={notificationDark}
            iconLight={notificationLight}
            label="Alerts"
            active={activeKey === "notifications"}
            onClick={() => { navigate("/notifications"); if (isMobile) setSidebarOpen(false); }}
          />
          <NavItem
            iconDark={feedbackDark}
            iconLight={feedbackLight}
            label="Feedback"
            active={activeKey === "feedback"}
            onClick={() => { navigate("/feedback"); if (isMobile) setSidebarOpen(false); }}
          />
        </nav>

        {/* BOTTOM — avatar + logout */}
        {compact ? (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 14,
              marginTop: "auto",
              marginBottom: 18,
            }}
          >
            <img
              src={adminAvatar}
              title="Admin · enquiry@itax.sg"
              style={{ width: 36, height: 36, borderRadius: "50%", objectFit: "cover" }}
              alt="Admin Avatar"
            />
            <button
              title="Log out"
              onClick={() => { navigate("/login"); if (isMobile) setSidebarOpen(false); }}
              style={{
                width: 36,
                height: 36,
                borderRadius: "50%",
                border: "1px solid var(--border)",
                background: "transparent",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                padding: 0,
              }}
            >
              <img
                src={theme === "dark" ? logoutDark : logoutLight}
                style={{ width: 18, height: 18 }}
                alt="Logout"
              />
            </button>
          </div>
        ) : (
          <div
            style={{
              width: 150,
              height: 234,
              borderRadius: 20,
              background: "var(--page-gradient)",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "space-between",
              padding: "20px 12px",
              alignSelf: "center",
              marginTop: "auto",
              marginBottom: "24px",
            }}
          >
            <img src={adminAvatar} style={{ width: 52, height: 52 }} alt="Admin Avatar" />
            <div style={{ textAlign: "center" }}>
              <div style={{ color: "var(--color)", fontWeight: 600 }}>Admin</div>
              <div style={{ fontSize: 12, color: "var(--color)" }}>enquiry@itax.sg</div>
            </div>
            <button
              onClick={() => { navigate("/login"); if (isMobile) setSidebarOpen(false); }}
              style={{
                width: 54,
                height: 54,
                borderRadius: "100%",
                border: "1px solid var(--border)",
                background: "transparent",
                cursor: "pointer",
              }}
            >
              <img
                src={theme === "dark" ? logoutDark : logoutLight}
                style={{ width: 28, height: 28 }}
                alt="Logout"
              />
            </button>
          </div>
        )}
      </aside>

      {showEngagementModal && (
        <div
          onClick={() => setShowEngagementModal(false)}
          style={{
            position: "fixed",
            inset: 0,
            backdropFilter: "blur(6px)",
            background: "rgba(0,0,0,0.45)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 10000,
          }}
        >
          <NewClientEngagementModal
            onCancel={() => setShowEngagementModal(false)}
            onCreateEngagement={handleCreateEngagement}
          />
        </div>
      )}

      {/* Dashboard redirect confirm */}
      {showDashboardConfirm && (
        <div
          onClick={cancelRedirect}
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.6)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 6000,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              width: 420,
              height: 200,
              background: "var(--bg-color)",
              borderRadius: 16,
              padding: 28,
              alignContent: "center",
              textAlign: "center",
              boxShadow: "var(--shadow-elevated)",
            }}
          >
            <div style={{ color: "var(--color)", fontSize: 16, fontWeight: 600, marginBottom: 24 }}>
              You will be redirected to your dashboard. Do you want to continue?
            </div>
            <div style={{ display: "flex", justifyContent: "center", gap: 16 }}>
              <button
                onClick={confirmRedirectToDashboard}
                style={{
                  padding: "10px 22px",
                  borderRadius: 999,
                  background: "var(--page-gradient)",
                  border: "none",
                  color: "var(--color)",
                  fontWeight: 600,
                  cursor: "pointer",
                }}
              >
                Yes
              </button>
              <button
                onClick={cancelRedirect}
                style={{
                  padding: "10px 22px",
                  borderRadius: 999,
                  background: "transparent",
                  border: "1px solid var(--border)",
                  color: "var(--color)",
                  cursor: "pointer",
                }}
              >
                No
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
